
import { useState } from 'react';
import { blogArticles, blogCategories } from '../../../mocks/blogArticles';
import { Link } from 'react-router-dom';
import useScrollAnimation from '../../../hooks/useScrollAnimation';

const BlogGrid = () => {
  const [activeCategory, setActiveCategory] = useState('Todos');
  const { ref: filtersRef, isVisible: filtersVisible } = useScrollAnimation({ threshold: 0.1 });
  const { ref: featuredRef, isVisible: featuredVisible } = useScrollAnimation({ threshold: 0.1 });
  const { ref: gridRef, isVisible: gridVisible } = useScrollAnimation({ threshold: 0.05 });

  const filteredArticles = activeCategory === 'Todos'
    ? blogArticles
    : blogArticles.filter(a => a.category === activeCategory);

  const featuredArticle = blogArticles.find(a => a.featured);
  const regularArticles = filteredArticles.filter(a => !a.featured || activeCategory !== 'Todos');

  return (
    <section className="py-16 px-6 lg:px-12 bg-gray-50">
      <div className="max-w-7xl mx-auto">
        {/* Filtros de categoría */}
        <div
          ref={filtersRef}
          className={`flex flex-wrap gap-3 mb-12 transition-all duration-700 ${
            filtersVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          {blogCategories.map((cat, index) => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`px-5 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer duration-500 ${
                filtersVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
              } ${
                activeCategory === cat
                  ? 'bg-emerald-600 text-white shadow-md'
                  : 'bg-white text-gray-600 hover:bg-emerald-50 hover:text-emerald-700 border border-gray-200'
              }`}
              style={{ transitionDelay: `${index * 60}ms` }}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Artículo destacado */}
        {activeCategory === 'Todos' && featuredArticle && (
          <Link
            to={`/blog/${featuredArticle.id}`}
            className="block mb-12 group"
          >
            <article
              ref={featuredRef}
              className={`bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-700 border-t-4 border-emerald-600 ${
                featuredVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              }`}
            >
              <div className="grid grid-cols-1 lg:grid-cols-2">
                <div
                  className={`relative w-full h-64 lg:h-96 overflow-hidden transition-all duration-700 delay-200 ${
                    featuredVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'
                  }`}
                >
                  <img
                    src={featuredArticle.image}
                    alt={featuredArticle.title}
                    title={`${featuredArticle.title} - Desguace Gavá`}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                  <span className="absolute top-4 left-4 px-3 py-1 bg-emerald-600 text-white text-xs font-bold rounded-full flex items-center space-x-1">
                    <i className="ri-fire-fill"></i>
                    <span>Destacado</span>
                  </span>
                </div>
                <div
                  className={`p-8 lg:p-12 flex flex-col justify-center transition-all duration-700 delay-300 ${
                    featuredVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'
                  }`}
                >
                  <div className="flex items-center space-x-3 mb-4">
                    <span className="text-emerald-600 text-xs font-bold uppercase tracking-wider">{featuredArticle.category}</span>
                    <span className="text-gray-300">|</span>
                    <span className="text-gray-400 text-xs flex items-center space-x-1">
                      <i className="ri-time-line"></i>
                      <span>{featuredArticle.readTime} lectura</span>
                    </span>
                  </div>
                  <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-4 group-hover:text-emerald-700 transition-colors leading-tight">
                    {featuredArticle.title}
                  </h2>
                  <p className="text-gray-600 leading-relaxed mb-6 text-sm lg:text-base">
                    {featuredArticle.excerpt}
                  </p>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="w-9 h-9 bg-emerald-100 rounded-full flex items-center justify-center">
                        <i className="ri-user-line text-emerald-700 text-sm"></i>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">{featuredArticle.author}</p>
                        <p className="text-xs text-gray-400">{featuredArticle.date}</p>
                      </div>
                    </div>
                    <span className="text-emerald-600 text-sm font-semibold flex items-center space-x-1 group-hover:translate-x-1 transition-transform">
                      <span>Leer más</span>
                      <i className="ri-arrow-right-line"></i>
                    </span>
                  </div>
                </div>
              </div>
            </article>
          </Link>
        )}

        {/* Grid de artículos */}
        <div ref={gridRef} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {(activeCategory === 'Todos' ? filteredArticles.filter(a => !a.featured) : filteredArticles).map((article, index) => (
            <Link
              key={article.id}
              to={`/blog/${article.id}`}
              className="group"
            >
              <article
                className={`bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-lg transition-all duration-500 hover:-translate-y-1 h-full flex flex-col ${
                  gridVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                }`}
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                <div className="relative w-full h-52 overflow-hidden">
                  <img
                    src={article.image}
                    alt={article.title}
                    title={`${article.title} - Desguace Gavá`}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                  <span className="absolute top-3 right-3 px-2.5 py-1 bg-black/60 text-white text-xs rounded-full flex items-center space-x-1">
                    <i className="ri-time-line text-[10px]"></i>
                    <span>{article.readTime}</span>
                  </span>
                </div>
                <div className="p-6 flex flex-col flex-1">
                  <div className="flex items-center space-x-3 mb-3">
                    <span className="text-emerald-600 text-xs font-bold uppercase tracking-wider">{article.category}</span>
                    <span className="text-gray-300">|</span>
                    <span className="text-gray-400 text-xs">{article.date}</span>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-3 group-hover:text-emerald-700 transition-colors leading-snug line-clamp-2">
                    <strong>{article.title}</strong>
                  </h3>
                  <p className="text-gray-500 text-sm leading-relaxed mb-5 line-clamp-3 flex-1">
                    {article.excerpt}
                  </p>
                  <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                    <div className="flex items-center space-x-2">
                      <div className="w-7 h-7 bg-emerald-100 rounded-full flex items-center justify-center">
                        <i className="ri-user-line text-emerald-700 text-xs"></i>
                      </div>
                      <span className="text-xs font-medium text-gray-700">{article.author}</span>
                    </div>
                    <span className="text-emerald-600 text-xs font-semibold flex items-center space-x-1 group-hover:translate-x-1 transition-transform">
                      <span>Leer</span>
                      <i className="ri-arrow-right-s-line"></i>
                    </span>
                  </div>
                </div>
              </article>
            </Link>
          ))}
        </div>

        {filteredArticles.length === 0 && (
          <div className="text-center py-16">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-article-line text-gray-400 text-2xl"></i>
            </div>
            <p className="text-gray-500 text-lg">No hay artículos en esta categoría todavía.</p>
          </div>
        )}
      </div>
    </section>
  );
};

export default BlogGrid;
